import { useEffect, useState } from "react";

import { AR_AGING_COLUMNS, AR_AGING_TABLE } from "./arAging.mjs";
import { ExecKpiFetchError, fetchAllPages, isTableMissingMessage } from "./execKpiFetch.mjs";
import { isColumnMissingError } from "./sanityGate";
import { LiveCache, useLiveCacheVersion } from "./liveCacheHook";
import { getSupabase } from "./supabase";
import { useTenantId } from "./tenant";
import { scopeTenant } from "./tenantScope.mjs";

/**
 * AR Aging tab: the one read behind every panel on it.
 *
 * ONE SNAPSHOT, ONE READ. The tab's nine panels all work on the same rows:
 * every account in the newest AR export. The read runs once per tenant per
 * page load and is shared (the promise is cached), so nine panels cost two
 * requests plus paging, not nine full reads.
 *
 * NEWEST IMPORT ONLY (MER-729). ar_current keeps every export as a dated
 * snapshot. Step one reads the newest import_date; step two reads only that
 * date's rows. Nothing here can add two snapshots together.
 *
 * Bound to the tenant through scopeTenant (MER-245) on top of RLS.
 */

/** Rows per page: PostgREST's default max-rows, so a full page means more follow. */
const PAGE_SIZE = 1000;
/** A ceiling of 50k accounts; past it the panel says the list is truncated. */
const MAX_PAGES = 50;

export interface ArAgingSnapshot {
  snapshotDate: string | null;
  rows: Record<string, unknown>[];
  truncated: boolean;
}

export type ArAgingState =
  | { kind: "loading" }
  | { kind: "ready"; snapshot: ArAgingSnapshot }
  | { kind: "missing" }
  | { kind: "error"; message: string };

function isMissing(message: string): boolean {
  return isTableMissingMessage(message) || isColumnMissingError(message);
}

async function fetchSnapshot(tenantId: string): Promise<ArAgingSnapshot> {
  const supabase = getSupabase();
  const latest = await scopeTenant(supabase.from(AR_AGING_TABLE).select("import_date"), tenantId)
    .not("import_date", "is", null)
    .order("import_date", { ascending: false })
    .limit(1);
  if (latest.error) {
    const message = latest.error.message ?? "Supabase error";
    throw new ExecKpiFetchError(message, isMissing(message));
  }
  const first = (latest.data as Record<string, unknown>[] | null)?.[0];
  const snapshotDate = first?.import_date ? String(first.import_date).slice(0, 10) : null;
  if (!snapshotDate) return { snapshotDate: null, rows: [], truncated: false };

  const result = await fetchAllPages<Record<string, unknown>>(
    (from: number, to: number) =>
      scopeTenant(supabase.from(AR_AGING_TABLE).select(AR_AGING_COLUMNS.join(",")), tenantId)
        .eq("import_date", snapshotDate)
        .order("account_id", { ascending: true })
        .range(from, to)
        .overrideTypes<Record<string, unknown>[], { merge: false }>(),
    { pageSize: PAGE_SIZE, maxPages: MAX_PAGES, isMissing },
  );
  return { snapshotDate, rows: result.rows, truncated: result.truncated };
}

const cache = new LiveCache<ArAgingSnapshot>();

/** Test hook: drop the shared read so the next mount fetches again. */
export function __resetArAgingCache(): void {
  cache.clear();
}

function sharedSnapshot(tenantId: string): Promise<ArAgingSnapshot> {
  // A failed read is not cached: the next mount tries again.
  return cache.get(tenantId, () => fetchSnapshot(tenantId));
}

/**
 * AJ 2026-09-30 live refresh: re-read the tenant's AR snapshot and swap it in
 * (useLiveReport). Rejects, keeping the old answer, when the read fails.
 */
export async function refreshArAging(tenantId: string): Promise<void> {
  await cache.refreshTenant(tenantId);
}

/** The newest AR snapshot for the current tenant, shared by every panel. */
export function useArAgingSnapshot(): ArAgingState {
  const tenantId = useTenantId();
  const [state, setState] = useState<ArAgingState>({ kind: "loading" });
  const version = useLiveCacheVersion(cache);
  useEffect(() => {
    // Fail closed: no tenant, no query (never an unscoped read).
    if (!tenantId) {
      setState({ kind: "loading" });
      return;
    }
    let cancelled = false;
    sharedSnapshot(tenantId).then(
      (snapshot) => {
        if (!cancelled) setState({ kind: "ready", snapshot });
      },
      (err: unknown) => {
        if (cancelled) return;
        if (err instanceof ExecKpiFetchError && err.tableMissing) {
          setState({ kind: "missing" });
        } else {
          setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [tenantId, version]);
  return state;
}
